/**
 * Superadmin read/write of this node's person-invite email wording — the
 * subject and body that InviteEmailModal pre-fills and NodeInviteEmail edits.
 *
 * Backed by GET/PUT/DELETE /api/node-settings/invite-email. Unlike
 * use_node_settings_admin.js this has no public read: the wording is internal,
 * so every route here is superadmin-gated and 403s for anyone else. Callers
 * mount inside already-superadmin-only panels, so a 403 is left to surface
 * rather than special-cased.
 *
 *   const { subject, body, placeholders, isCustom, loading, save, reset } = useInviteEmailTemplate();
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from './use_api';
import { useAuth } from '../context/AuthContext';

export function useInviteEmailTemplate() {
    const api = useApi();
    const { user } = useAuth();
    const userId = user?.id;

    const [template, setTemplate] = useState({ subject: '', body: '', placeholders: [], is_custom: false });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        if (!userId) return Promise.resolve(null);
        setLoading(true);
        return api.get('/api/node-settings/invite-email', { params: { user_id: userId } })
            .then(data => { setTemplate(data); setError(null); return data; })
            .catch(err => { setError(err); return null; })
            .finally(() => setLoading(false));
    }, [api, userId]);

    useEffect(() => { load(); }, [load]);

    const save = useCallback((subject, body) => api.put(
        '/api/node-settings/invite-email', { subject, body }, { params: { user_id: userId } },
    ).then(data => { setTemplate(data); return data; }), [api, userId]);

    const reset = useCallback(() => api.del(
        '/api/node-settings/invite-email', { params: { user_id: userId } },
    ).then(data => { setTemplate(data); return data; }), [api, userId]);

    return {
        subject: template?.subject || '',
        body: template?.body || '',
        placeholders: template?.placeholders || [],
        isCustom: !!template?.is_custom,
        loading,
        error,
        save,
        reset,
        reload: load,
    };
}

export default useInviteEmailTemplate;
